/**
 * Default language code used throughout the application when no language is specified.
 * This should match the sourceLocale in angular.json and be one of the SUPPORTED_LANGUAGES.
 */
export const DEFAULT_LANGUAGE = 'en-US';

export const SUPPORTED_LANGUAGES = [
  'en-US',
  'de-DE', 
  'sv-SE',
  'es-ES'
] as const;

/**
 * Type for supported language codes to ensure type safety.
 */
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Language options with display names for dropdowns and UI components.
 */
export const LANGUAGE_OPTIONS = [
  { 
    code: 'en-US' as SupportedLanguage, 
    name: $localize`:Language name\: English: English` 
  },
  { 
    code: 'de-DE' as SupportedLanguage, 
    name: $localize`:Language name\: German: German` 
  },
  { 
    code: 'sv-SE' as SupportedLanguage, 
    name: $localize`:Language name\: Swedish: Swedish` 
  },
  { 
    code: 'es-ES' as SupportedLanguage, 
    name: $localize`:Language name\: Spanish: Spanish` 
  }
];

/**
 * Validates if a language code is supported by the application.
 * @param languageCode - Language code to validate
 * @returns True if the language is supported, false otherwise
 */
export function isSupportedLanguage(languageCode: string): languageCode is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(languageCode as SupportedLanguage);
}

/**
 * Gets the default language or validates and returns a supported language.
 * Falls back to DEFAULT_LANGUAGE if the provided language is not supported.
 * @param languageCode - Language code to validate
 * @returns A supported language code
 */
export function getSafeLanguage(languageCode?: string | null): SupportedLanguage {
  if (languageCode && isSupportedLanguage(languageCode)) {
    return languageCode;
  }
  return DEFAULT_LANGUAGE;
}
